import React from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { toPairs, compose, sortBy, prop, map, prepend } from 'ramda'

import { INTERNAL, SERVICE } from 'constants/featureTypes'
import strings from 'utils/strings.js'
import { Flex } from 'components/patterns/Layout'

import { featureType } from 'i18n/displayConstants'
import featureMetaSchema from 'schemas/validation/feature/featureMetaSchema'
import { schemaProp } from 'schemas/validation'
import {
  RadioButtonGroupStyled,
  ReleaseNumberInputStyled,
  StyledSubgroupSelectMenu,
  NumberInputStyled,
  TextInputMaskStyled,
} from './FeatureEditor.style.js'

const rule = schemaProp(featureMetaSchema)

const StyledDiv = styled.div`
  margin-left: 10px;
  margin-top: 2px;
`

const featureTypeOptions = [
  {
    text: featureType(SERVICE),
    label: featureType(SERVICE),
    value: SERVICE,
  },
  {
    text: featureType(INTERNAL),
    label: featureType(INTERNAL),
    value: INTERNAL,
  },
]

const FeatureEdit = ({
  releaseNumber,
  subGroup,
  priority,
  successorCode,
  updateReleaseNumber,
  updateSubGroup,
  updateSuccessorCode,
  updatePriority,
  type,
  updateFeatureType,
  subGroups,
  canCreateInternalFeature,
  isInternalFeature,
  featureId,
  isPreCreate,
  isRevision,
}) => {
  const unselectSubGroupOption = {
    key: 'unselect',
    value: '',
    title: '---',
  }

  const formatSubGroupOption = ([id, description]) => ({
    key: id,
    value: id,
    title: `${id} - ${description}`,
  })

  const subGroupOptions = compose(
    prepend(unselectSubGroupOption),
    map(formatSubGroupOption),
    sortBy(prop(1)),
    toPairs
  )(subGroups)

  return (
    <>
      {canCreateInternalFeature && (
        <Flex>
          <RadioButtonGroupStyled
            isPreCreate={isPreCreate}
            label={strings.feature_type}
            currentValue={type}
            buttons={featureTypeOptions}
            onChange={(event) => updateFeatureType(event, featureId)}
            disabled={isRevision}
            className="feature-type-selector"
            vertical
            tooltip={strings.feature_edit_type_tooltip}
            icon="question_mark_tooltip"
          />

          {isInternalFeature && (
            <StyledDiv>
              <ReleaseNumberInputStyled
                isPreCreate={isPreCreate}
                label={strings.release_number}
                className="feature-release-input form-field"
                onChange={(value) => updateReleaseNumber(value, featureId)}
                disabled={isRevision}
                value={releaseNumber}
                width={120}
                name="release number"
                rules={rule('releaseNumber')}
              />
            </StyledDiv>
          )}
        </Flex>
      )}

      <Flex>
        <StyledSubgroupSelectMenu
          className="form-field"
          rules={rule('subGroup', { type })}
          isPreCreate={isPreCreate}
          value={subGroup}
          options={subGroupOptions}
          onChange={(event) => updateSubGroup(event, featureId)}
          disabled={isRevision}
          label={strings.subgroup}
          name={strings.subgroup}
          showSearch={true}
          filterOption
          showArrow
          vertical
          verticalStyle={{ width: '100%' }}
          style={{ width: '100%' }}
          tooltip={strings.feature_edit_subgroup_tooltip}
          icon="question_mark_tooltip"
        />
      </Flex>
      {canCreateInternalFeature && isInternalFeature && (
        <>
          <Flex>
            <NumberInputStyled
              className="form-field"
              rules={rule('priority')}
              isPreCreate={isPreCreate}
              value={priority}
              onChange={(value) => updatePriority(value, featureId)}
              disabled={isRevision}
              name={strings.priority}
              label={strings.priority}
              hint={strings.feature_edit_priority_hint}
              width={110}
              tooltip={strings.feature_edit_priority_tooltip}
              icon="question_mark_tooltip"
            />
          </Flex>
          <Flex>
            <TextInputMaskStyled
              className="form-field"
              rules={rule('successorCode')}
              isPreCreate={isPreCreate}
              value={successorCode}
              onChange={(event) => updateSuccessorCode(event, featureId)}
              disabled={isRevision}
              mask="***-***"
              label={strings.successor_code}
              name={strings.successor_code}
              width={110}
              tooltip={strings.feature_edit_successor_code_tooltip}
              icon="question_mark_tooltip"
            />
          </Flex>
        </>
      )}
    </>
  )
}

FeatureEdit.propTypes = {
  releaseNumber: PropTypes.string,
  subGroup: PropTypes.string,
  priority: PropTypes.number,
  successorCode: PropTypes.string,
  updateReleaseNumber: PropTypes.func,
  updateSubGroup: PropTypes.func,
  updateSuccessorCode: PropTypes.func,
  updatePriority: PropTypes.func,
  type: PropTypes.string,
  updateFeatureType: PropTypes.func,
  subGroups: PropTypes.object,
  canCreateInternalFeature: PropTypes.bool,
  isInternalFeature: PropTypes.bool,
  featureId: PropTypes.string,
  isPreCreate: PropTypes.bool,
  isRevision: PropTypes.bool,
}

export default FeatureEdit
